import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Modal, Tabs, Input, Tooltip, Spin } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  ReloadOutlined,
  EyeOutlined,
  UserOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { NODE_API_BASE_URL } from "../../config";
import { approvePayment, rejectPayment } from "../services/orderService";

const PAGE_LIMIT = 10;

// ── Role helper ───────────────────────────────────────────────────────────────
const getRole = () => (localStorage.getItem("trade_user_type") || "").toLowerCase();


const token = () => localStorage.getItem("cms_token");

// ── Fetch helpers ─────────────────────────────────────────────────────────────
const fetchPage = async (status, page) => {
  const res = await axios.get(`${NODE_API_BASE_URL}/api/subscription/get-customer-list-v3`, {
    params: { customer_status: status, page, limit: PAGE_LIMIT },
    headers: { Authorization: `Bearer ${token()}` },
  });
  return {
    data: res.data?.data || [],
    total: res.data?.total || 0,
    lastPage: res.data?.last_page || 1,
  };
};

// ── Proof image thumbnail ─────────────────────────────────────────────────────
const ProofThumb = ({ url, size = 48 }) => {
  const [open, setOpen] = useState(false);
  if (!url) return <span style={{ color: "#94a3b8", fontSize: 11 }}>No proof</span>;
  return (
    <>
      <img
        src={url}
        alt="proof"
        onClick={() => setOpen(true)}
        style={{
          width: size, height: size, objectFit: "cover", borderRadius: 6,
          border: "2px solid #e2e8f0", cursor: "pointer",
          transition: "transform 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.08)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        title="Click to enlarge"
      />
      <Modal open={open} footer={null} onCancel={() => setOpen(false)} width={700} centered>
        <img src={url} alt="payment proof" style={{ width: "100%", borderRadius: 8 }} />
      </Modal>
    </>
  );
};

// ── Reject modal ──────────────────────────────────────────────────────────────
const RejectModal = ({ open, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState("");
  useEffect(() => { if (!open) setReason(""); }, [open]);
  const handleConfirm = () => {
    if (!reason.trim()) { toast.warning("Please enter a rejection reason."); return; }
    onConfirm(reason.trim());
  };
  return (
    <Modal
      open={open}
      title={<span style={{ color: "#dc2626", fontWeight: 700 }}>Reject Payment</span>}
      onCancel={onClose}
      onOk={handleConfirm}
      okText="Reject"
      okButtonProps={{ danger: true, loading }}
      cancelButtonProps={{ disabled: loading }}
      centered
    >
      <p style={{ color: "#374151", marginBottom: 4, fontWeight: 600 }}>
        Are you sure you want to reject this payment?
      </p>
      <p style={{ color: "#64748b", fontSize: 13, marginBottom: 10 }}>Please provide a reason for rejection:</p>
      <Input.TextArea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Payment proof is unclear / amount mismatch"
        maxLength={500}
        showCount
      />
    </Modal>
  );
};

// ── Shared approve/reject logic hook ─────────────────────────────────────────
const useActions = (item, onRefetch) => {
  const [approving, setApproving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const handleApprove = () => {
    Modal.confirm({
      title: <span style={{ fontWeight: 700, fontSize: 15 }}>Approve Payment?</span>,
      content: (
        <div style={{ color: "#475569", marginTop: 4 }}>
          Are you sure you want to approve this payment?
          <br />
          <span style={{ color: "#16a34a", fontWeight: 600 }}>The subscription will be activated immediately.</span>
        </div>
      ),
      okText: "Yes, Approve",
      okButtonProps: { style: { background: "#16a34a", borderColor: "#16a34a" } },
      cancelText: "Cancel",
      centered: true,
      icon: <CheckCircleOutlined style={{ color: "#16a34a" }} />,
      onOk: async () => {
        setApproving(true);
        try {
          await approvePayment({ orderId: item.latest_order_id });
          toast.success("Payment approved — subscription activated.");
          onRefetch();
        } catch (err) {
          const status = err?.response?.status;
          const msg = err?.response?.data?.message;
          if (status === 409 || status === 404) onRefetch();
          else toast.error(msg || "Failed to approve payment.");
        } finally {
          setApproving(false);
        }
      },
    });
  };

  const handleReject = async (reason) => {
    setRejecting(true);
    try {
      await rejectPayment({ orderId: item.latest_order_id, rejectionReason: reason });
      toast.success("Payment rejected.");
      setRejectOpen(false);
      onRefetch();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      if (status === 409 || status === 404) { setRejectOpen(false); onRefetch(); }
      else toast.error(msg || "Failed to reject payment.");
    } finally {
      setRejecting(false);
    }
  };

  return { approving, rejectOpen, setRejectOpen, rejecting, handleApprove, handleReject };
};

const modeLabel = (m) => (m || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ── List row (default view) ───────────────────────────────────────────────────
const PaymentRow = ({ item, isRejected, canAct, onRefetch, idx }) => {
  const { approving, rejectOpen, setRejectOpen, rejecting, handleApprove, handleReject } = useActions(item, onRefetch);
  const isManualPending = item.latest_payment_status === "0" && item.latest_approval_status === "pending";
  const isOnlineLink    = item.latest_payment_status === "2";

  return (
    <>
      <div style={{
        display: "grid",
        gridTemplateColumns: "28px 200px 120px 110px 110px 130px 110px 60px auto",
        alignItems: "center",
        gap: 14,
        padding: "12px 16px",
        background: idx % 2 === 0 ? "#fff" : "#fafbff",
        borderBottom: "1px solid #f1f5f9",
        borderLeft: `3px solid ${isRejected ? "#ef4444" : "#6366f1"}`,
        transition: "background 0.15s",
      }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f4ff")}
        onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbff")}
      >
        {/* Index */}
        <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600, textAlign: "center" }}>{idx + 1}</span>

        {/* Customer */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
            background: "linear-gradient(135deg,#6366f1,#4f46e5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 12,
          }}>
            {(item.customer_name || "U")[0]?.toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {item.customer_name || "—"}
            </div>
            <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {item.customer_email || ""}
            </div>
          </div>
        </div>

        {/* Plan */}
        <div>
          <span style={{ background: "#f0f4ff", color: "#6366f1", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
            {item.plan_name || "—"}
          </span>
          {item.change_type && (
            <span style={{
              display: "inline-block", marginTop: 3,
              fontSize: 10, fontWeight: 700,
              color: item.change_type === "upgrade" ? "#7c3aed" : "#2563eb",
              background: item.change_type === "upgrade" ? "#f5f3ff" : "#eff6ff",
              borderRadius: 4, padding: "1px 6px",
            }}>
              {item.change_type === "upgrade" ? "↑ Upgrade" : "✦ New"}
            </span>
          )}
        </div>

        {/* Amount */}
        <div>
          {item.latest_order_amount != null ? (
            <span style={{ background: "#f0fdf4", color: "#16a34a", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
              ₹{Number(item.latest_order_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </span>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 11 }}>—</span>
          )}
        </div>

        {/* Mode */}
        <div style={{ fontSize: 11, color: "#475569" }}>
          {item.latest_payment_mode
            ? <span style={{ background: "#f0f4ff", color: "#4f46e5", borderRadius: 4, padding: "2px 7px", fontWeight: 600 }}>{modeLabel(item.latest_payment_mode)}</span>
            : <span style={{ color: "#cbd5e1" }}>—</span>}
        </div>

        {/* UTR / Ref No. */}
        <div style={{ fontSize: 11, color: "#475569", wordBreak: "break-all" }}>
          {item.latest_utr || <span style={{ color: "#cbd5e1" }}>—</span>}
        </div>

        {/* Bank */}
        <div style={{ fontSize: 11, color: "#475569" }}>
          {item.latest_bank_name
            ? <span>{item.latest_bank_name}</span>
            : <span style={{ color: "#cbd5e1" }}>—</span>}
          {isRejected && item.rejection_reason && (
            <div style={{ color: "#dc2626", fontSize: 10, marginTop: 2 }} title={item.rejection_reason}>
              Rejected: {item.rejection_reason.length > 30 ? item.rejection_reason.slice(0, 30) + "…" : item.rejection_reason}
            </div>
          )}
        </div>

        {/* Proof */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ProofThumb url={item.payment_proof_url} size={40} />
        </div>

        {/* Actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <Tooltip title="View Profile">
            <Link
              to={`/cms/manageUserData/user-profile/${item.user_id}`}
              style={{
                width: 28, height: 28, borderRadius: 6, border: "1px solid #c7d2fe",
                background: "#eef2ff", display: "inline-flex", alignItems: "center",
                justifyContent: "center", color: "#6366f1", fontSize: 12,
              }}
            >
              <EyeOutlined />
            </Link>
          </Tooltip>
          {canAct && !isRejected && isManualPending && (
            <>
              <Tooltip title="Approve">
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  style={{
                    width: 28, height: 28, borderRadius: 6, border: "none",
                    background: approving ? "#d1fae5" : "linear-gradient(135deg,#22c55e,#16a34a)",
                    color: "#fff", cursor: approving ? "not-allowed" : "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                  }}
                >
                  {approving ? <Spin size="small" /> : <CheckCircleOutlined />}
                </button>
              </Tooltip>
              <Tooltip title="Reject">
                <button
                  onClick={() => setRejectOpen(true)}
                  disabled={approving}
                  style={{
                    width: 28, height: 28, borderRadius: 6,
                    border: "1px solid #fecaca", background: "#fef2f2",
                    color: "#dc2626", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                  }}
                >
                  <CloseCircleOutlined />
                </button>
              </Tooltip>
            </>
          )}
          {isOnlineLink && !isRejected && (
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
                background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa",
                whiteSpace: "nowrap",
              }}>
                <CreditCardOutlined style={{ marginRight: 3 }} />Auto
              </span>
              {item.payment_link && (
                <Tooltip title="Copy payment link">
                  <button
                    onClick={() => { navigator.clipboard.writeText(item.payment_link); toast.success("Payment link copied!"); }}
                    style={{
                      width: 28, height: 28, borderRadius: 6,
                      border: "1px solid #e0e7ff", background: "#f0f4ff",
                      color: "#4f46e5", cursor: "pointer",
                      display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12,
                    }}
                  >
                    <i className="bx bx-copy" />
                  </button>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>

      <RejectModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={handleReject}
        loading={rejecting}
      />
    </>
  );
};

// ── Card view ─────────────────────────────────────────────────────────────────
const PaymentCard = ({ item, isRejected, canAct, onRefetch }) => {
  const { approving, rejectOpen, setRejectOpen, rejecting, handleApprove, handleReject } = useActions(item, onRefetch);
  const isManualPending = item.latest_payment_status === "0" && item.latest_approval_status === "pending";
  const isOnlineLink    = item.latest_payment_status === "2";

  return (
    <div style={{
      background: "#fff", borderRadius: 14, padding: "18px 20px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
      border: `1.5px solid ${isRejected ? "#fecaca" : "#e0f2fe"}`,
      borderLeft: `4px solid ${isRejected ? "#ef4444" : "#6366f1"}`,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: "50%",
            background: "linear-gradient(135deg,#6366f1,#4f46e5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 14, flexShrink: 0,
          }}>
            {(item.customer_name || "U")[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{item.customer_name || "—"}</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{item.customer_email || ""}</div>
            {item.mobile && <div style={{ fontSize: 11, color: "#94a3b8" }}>{item.mobile}</div>}
          </div>
        </div>
        <Tooltip title="View Profile">
          <Link
            to={`/cms/manageUserData/user-profile/${item.user_id}`}
            style={{
              width: 30, height: 30, borderRadius: 7, border: "1px solid #c7d2fe",
              background: "#eef2ff", display: "inline-flex", alignItems: "center",
              justifyContent: "center", color: "#6366f1", fontSize: 13,
            }}
          >
            <EyeOutlined />
          </Link>
        </Tooltip>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <span style={{ background: "#f0f4ff", color: "#6366f1", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
          {item.plan_name || "—"}
        </span>
        {item.change_type && (
          <span style={{
            fontSize: 11, fontWeight: 700,
            color: item.change_type === "upgrade" ? "#7c3aed" : "#2563eb",
            background: item.change_type === "upgrade" ? "#f5f3ff" : "#eff6ff",
            borderRadius: 5, padding: "2px 8px",
          }}>
            {item.change_type === "upgrade" ? "↑ Upgrade" : "✦ New"}
          </span>
        )}
        {item.latest_order_amount != null && (
          <span style={{ background: "#f0fdf4", color: "#16a34a", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
            ₹{Number(item.latest_order_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </span>
        )}
      </div>

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 12 }}>
        <ProofThumb url={item.payment_proof_url} />
        <div style={{ flex: 1, fontSize: 12, color: "#475569", lineHeight: 1.8 }}>
          {item.latest_payment_mode && <div><b>Mode:</b> {modeLabel(item.latest_payment_mode)}</div>}
          {item.latest_utr && <div><b>UTR:</b> {item.latest_utr}</div>}
          {item.latest_bank_name && <div><b>Bank:</b> {item.latest_bank_name}</div>}
        </div>
      </div>

      {isRejected && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fecaca",
          borderRadius: 8, padding: "10px 12px", marginBottom: 12,
        }}>
          <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 600, marginBottom: 3 }}>Rejection Reason:</div>
          <div style={{ fontSize: 12, color: "#7f1d1d" }}>{item.rejection_reason || "—"}</div>
          {item.verified_by && (
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
              Rejected by {item.verified_by}{item.verified_at ? ` on ${item.verified_at}` : ""}
            </div>
          )}
        </div>
      )}

      {canAct && !isRejected && isManualPending && (
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button
            onClick={handleApprove}
            disabled={approving}
            style={{
              flex: 1, padding: "8px 0", borderRadius: 8, border: "none",
              background: approving ? "#d1fae5" : "linear-gradient(135deg,#22c55e,#16a34a)",
              color: "#fff", fontWeight: 700, fontSize: 13, cursor: approving ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            {approving ? <Spin size="small" /> : <CheckCircleOutlined />}
            {approving ? "Approving…" : "Approve"}
          </button>
          <button
            onClick={() => setRejectOpen(true)}
            disabled={approving}
            style={{
              flex: 1, padding: "8px 0", borderRadius: 8,
              border: "1.5px solid #fecaca", background: "#fef2f2",
              color: "#dc2626", fontWeight: 700, fontSize: 13, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <CloseCircleOutlined /> Reject
          </button>
        </div>
      )}

      {isOnlineLink && !isRejected && (
        <div style={{
          marginTop: 8, padding: "10px 12px", borderRadius: 8,
          background: "#fff7ed", border: "1px solid #fed7aa",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <CreditCardOutlined style={{ color: "#c2410c", fontSize: 15 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#c2410c" }}>Awaiting online payment</div>
              <div style={{ fontSize: 11, color: "#9a3412" }}>Auto-approved by payment gateway on success</div>
            </div>
          </div>
          {item.payment_link && (
            <button
              onClick={() => { navigator.clipboard.writeText(item.payment_link); toast.success("Payment link copied!"); }}
              style={{
                padding: "5px 12px", borderRadius: 6, border: "1px solid #fed7aa",
                background: "#fff", color: "#c2410c", fontSize: 12, fontWeight: 600,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
              }}
            >
              <i className="bx bx-copy" style={{ fontSize: 13 }} /> Copy Link
            </button>
          )}
        </div>
      )}

      {!canAct && !isRejected && isManualPending && (
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4, fontStyle: "italic" }}>
          Read-only — admin approval required
        </div>
      )}

      <RejectModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={handleReject}
        loading={rejecting}
      />
    </div>
  );
};

// ── Approved list row ─────────────────────────────────────────────────────────
const ApprovedRow = ({ item, idx }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "28px 200px 120px 110px 110px 130px 110px 160px 140px 100px",
      alignItems: "center",
      gap: 14,
      padding: "12px 16px",
      background: idx % 2 === 0 ? "#fff" : "#fafbff",
      borderBottom: "1px solid #f1f5f9",
      borderLeft: "3px solid #16a34a",
      transition: "background 0.15s",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = "#f0fdf4")}
    onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbff")}
  >
    <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600, textAlign: "center" }}>{idx + 1}</span>

    {/* Customer */}
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <div style={{
        width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
        background: "linear-gradient(135deg,#16a34a,#22c55e)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontWeight: 700, fontSize: 12,
      }}>
        {(item.customer_name || "U")[0]?.toUpperCase()}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.customer_name || "—"}
        </div>
        <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.customer_email || ""}
        </div>
        {item.mobile && <div style={{ fontSize: 10, color: "#94a3b8" }}>{item.mobile}</div>}
      </div>
    </div>

    {/* Plan */}
    <div>
      <span style={{ background: "#f0fdf4", color: "#16a34a", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
        {item.plan_name || "—"}
      </span>
    </div>

    {/* Amount */}
    <div>
      {item.latest_order_amount != null ? (
        <span style={{ background: "#f0fdf4", color: "#16a34a", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
          ₹{Number(item.latest_order_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
        </span>
      ) : <span style={{ color: "#94a3b8", fontSize: 11 }}>—</span>}
    </div>

    {/* Mode */}
    <div style={{ fontSize: 11, color: "#475569" }}>
      {item.latest_payment_mode
        ? <span style={{ background: "#f0fdf4", color: "#16a34a", borderRadius: 4, padding: "2px 7px", fontWeight: 600 }}>{modeLabel(item.latest_payment_mode)}</span>
        : <span style={{ color: "#cbd5e1" }}>—</span>}
    </div>

    {/* UTR */}
    <div style={{ fontSize: 11, color: "#475569", wordBreak: "break-all" }}>
      {item.latest_utr || <span style={{ color: "#cbd5e1" }}>—</span>}
    </div>

    {/* Bank */}
    <div style={{ fontSize: 11, color: "#475569" }}>
      {item.latest_bank_name || <span style={{ color: "#cbd5e1" }}>—</span>}
    </div>

    {/* Approved By */}
    <div style={{ fontSize: 11, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {item.verified_by
        ? <Tooltip title={item.verified_by}><span style={{ color: "#16a34a", fontWeight: 600 }}>{item.verified_by}</span></Tooltip>
        : <span style={{ color: "#cbd5e1" }}>—</span>}
    </div>

    {/* Approved On */}
    <div style={{ fontSize: 11, color: "#475569" }}>
      {item.verified_at || <span style={{ color: "#cbd5e1" }}>—</span>}
    </div>

    {/* Ordered On */}
    <div style={{ fontSize: 11, color: "#64748b" }}>
      {item.latest_order_created_at || <span style={{ color: "#cbd5e1" }}>—</span>}
    </div>
  </div>
);

// ── Approved card view ────────────────────────────────────────────────────────
const ApprovedCard = ({ item }) => (
  <div style={{
    background: "#fff", borderRadius: 14, padding: "18px 20px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    border: "1.5px solid #bbf7d0",
    borderLeft: "4px solid #16a34a",
  }}>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: "50%",
          background: "linear-gradient(135deg,#16a34a,#22c55e)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 700, fontSize: 14, flexShrink: 0,
        }}>
          {(item.customer_name || "U")[0]?.toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{item.customer_name || "—"}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{item.customer_email || ""}</div>
          {item.mobile && <div style={{ fontSize: 11, color: "#94a3b8" }}>{item.mobile}</div>}
        </div>
      </div>
      <Tooltip title="View Profile">
        <Link
          to={`/cms/manageUserData/user-profile/${item.user_id}`}
          style={{
            width: 30, height: 30, borderRadius: 7, border: "1px solid #bbf7d0",
            background: "#f0fdf4", display: "inline-flex", alignItems: "center",
            justifyContent: "center", color: "#16a34a", fontSize: 13,
          }}
        >
          <EyeOutlined />
        </Link>
      </Tooltip>
    </div>

    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
      <span style={{ background: "#f0fdf4", color: "#16a34a", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
        {item.plan_name || "—"}
      </span>
      {item.latest_order_amount != null && (
        <span style={{ background: "#f0fdf4", color: "#16a34a", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
          ₹{Number(item.latest_order_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
        </span>
      )}
    </div>

    <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.8, marginBottom: 12 }}>
      {item.latest_payment_mode && <div><b>Mode:</b> {modeLabel(item.latest_payment_mode)}</div>}
      {item.latest_utr && <div><b>UTR:</b> {item.latest_utr}</div>}
      {item.latest_bank_name && <div><b>Bank:</b> {item.latest_bank_name}</div>}
      {item.latest_order_created_at && <div><b>Ordered On:</b> {item.latest_order_created_at}</div>}
    </div>

    <div style={{
      background: "#f0fdf4", border: "1px solid #bbf7d0",
      borderRadius: 8, padding: "10px 12px",
    }}>
      <div style={{ fontSize: 12, color: "#16a34a", fontWeight: 600, marginBottom: 3 }}>Approved</div>
      {item.verified_by && (
        <div style={{ fontSize: 12, color: "#166534" }}>
          By: <b>{item.verified_by}</b>
        </div>
      )}
      {item.verified_at && (
        <div style={{ fontSize: 11, color: "#4ade80", marginTop: 2 }}>
          On: {item.verified_at}
        </div>
      )}
    </div>
  </div>
);

// ── Approved column ───────────────────────────────────────────────────────────
const ApprovedColumn = ({ viewMode }) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  const loadFirst = useCallback(async () => {
    setLoading(true);
    setItems([]);
    setPage(1);
    setHasMore(true);
    try {
      const res = await fetchPage("payment_approved", 1);
      setItems(res.data);
      setHasMore(res.data.length === PAGE_LIMIT && res.lastPage > 1);
      setPage(2);
    } catch {
      setItems([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchPage("payment_approved", page);
      if (res.data.length === 0) {
        setHasMore(false);
      } else {
        setItems((prev) => [...prev, ...res.data]);
        setHasMore(res.data.length === PAGE_LIMIT);
        setPage((p) => p + 1);
      }
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore]);

  useEffect(() => { loadFirst(); }, [loadFirst]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) loadMore();
      },
      { threshold: 0.1 }
    );
    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, loadMore]);

  const listHeader = (
    <div style={{
      display: "grid",
      gridTemplateColumns: "28px 200px 120px 110px 110px 130px 110px 160px 140px 100px",
      gap: 14,
      padding: "10px 16px",
      background: "#f0fdf4",
      borderBottom: "2px solid #bbf7d0",
      borderLeft: "3px solid transparent",
      fontSize: 11,
      fontWeight: 700,
      color: "#166534",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    }}>
      <span>#</span>
      <span>Customer</span>
      <span>Plan</span>
      <span>Amount</span>
      <span>Mode</span>
      <span>UTR / Ref No.</span>
      <span>Bank</span>
      <span>Approved By</span>
      <span>Approved On</span>
      <span>Ordered On</span>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 12 }}>
        {items.length > 0 && (
          <span style={{ fontSize: 12, color: "#94a3b8" }}>
            Showing {items.length}{hasMore ? "+" : ""} record{items.length !== 1 ? "s" : ""}
          </span>
        )}
        <button
          onClick={loadFirst}
          disabled={loading}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: "none", border: "1px solid #bbf7d0",
            borderRadius: 8, padding: "5px 12px", cursor: loading ? "not-allowed" : "pointer",
            fontSize: 12, color: "#16a34a",
          }}
        >
          <ReloadOutlined spin={loading} /> Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
          <CheckCircleOutlined style={{ fontSize: 40, marginBottom: 12, display: "block", color: "#bbf7d0" }} />
          No approved payments
        </div>
      ) : viewMode === "list" ? (
        <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #bbf7d0" }}>
          {listHeader}
          {items.map((item, idx) => (
            <ApprovedRow key={item.latest_order_id || item.user_id} item={item} idx={idx} />
          ))}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
          {items.map((item) => (
            <ApprovedCard key={item.latest_order_id || item.user_id} item={item} />
          ))}
        </div>
      )}

      <div ref={sentinelRef} style={{ height: 1 }} />

      {loadingMore && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Spin size="default" />
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>Loading more…</div>
        </div>
      )}

      {!hasMore && items.length > 0 && (
        <div style={{ textAlign: "center", padding: "16px 0", fontSize: 12, color: "#cbd5e1" }}>
          — All {items.length} records loaded —
        </div>
      )}
    </div>
  );
};

// ── Board column with infinite scroll ────────────────────────────────────────
const BoardColumn = ({ isRejected, canAct, viewMode }) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const status = isRejected ? "payment_rejected" : "payment_pending";

  const loadFirst = useCallback(async () => {
    setLoading(true);
    setItems([]);
    setPage(1);
    setHasMore(true);
    try {
      const res = await fetchPage(status, 1);
      setItems(res.data);
      setHasMore(res.data.length === PAGE_LIMIT && res.lastPage > 1);
      setPage(2);
    } catch {
      setItems([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [status]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchPage(status, page);
      if (res.data.length === 0) {
        setHasMore(false);
      } else {
        setItems((prev) => [...prev, ...res.data]);
        setHasMore(res.data.length === PAGE_LIMIT);
        setPage((p) => p + 1);
      }
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [status, page, hasMore, loadingMore]);

  useEffect(() => { loadFirst(); }, [loadFirst]);

  // IntersectionObserver — fires when sentinel scrolls into view
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, loadMore]);

  const listHeader = (
    <div style={{
      display: "grid",
      gridTemplateColumns: "28px 200px 120px 110px 110px 130px 110px 60px auto",
      gap: 14,
      padding: "10px 16px",
      background: "#f8fafc",
      borderBottom: "2px solid #e2e8f0",
      borderLeft: "3px solid transparent",
      fontSize: 11,
      fontWeight: 700,
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    }}>
      <span>#</span>
      <span>Customer</span>
      <span>Plan</span>
      <span>Amount</span>
      <span>Mode</span>
      <span>UTR / Ref No.</span>
      <span>Bank</span>
      <span style={{ textAlign: "center" }}>Proof</span>
      <span>Actions</span>
    </div>
  );

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 12 }}>
        {items.length > 0 && (
          <span style={{ fontSize: 12, color: "#94a3b8" }}>
            Showing {items.length}{hasMore ? "+" : ""} record{items.length !== 1 ? "s" : ""}
          </span>
        )}
        <button
          onClick={loadFirst}
          disabled={loading}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: "none", border: "1px solid #e2e8f0",
            borderRadius: 8, padding: "5px 12px", cursor: loading ? "not-allowed" : "pointer",
            fontSize: 12, color: "#64748b",
          }}
        >
          <ReloadOutlined spin={loading} /> Refresh
        </button>
      </div>

      {/* Initial loading */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
          <CreditCardOutlined style={{ fontSize: 40, marginBottom: 12, display: "block" }} />
          {isRejected ? "No rejected payments" : "No pending payments"}
        </div>
      ) : viewMode === "list" ? (
        <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #e2e8f0" }}>
          {listHeader}
          {items.map((item, idx) => (
            <PaymentRow
              key={item.latest_order_id || item.user_id}
              item={item}
              isRejected={isRejected}
              canAct={canAct}
              onRefetch={loadFirst}
              idx={idx}
            />
          ))}
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 16,
        }}>
          {items.map((item) => (
            <PaymentCard
              key={item.latest_order_id || item.user_id}
              item={item}
              isRejected={isRejected}
              canAct={canAct}
              onRefetch={loadFirst}
            />
          ))}
        </div>
      )}

      {/* Sentinel for infinite scroll */}
      <div ref={sentinelRef} style={{ height: 1 }} />

      {/* Load-more spinner */}
      {loadingMore && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Spin size="default" />
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>Loading more…</div>
        </div>
      )}

      {/* End of list indicator */}
      {!hasMore && items.length > 0 && (
        <div style={{ textAlign: "center", padding: "16px 0", fontSize: 12, color: "#cbd5e1" }}>
          — All {items.length} records loaded —
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const PaymentApproval = () => {
  const menuAccess = useSelector((state) => state.user.user?.menu_access || []);
  const canAct = menuAccess
    .flatMap((m) => m.sub_menus || [])
    .find((s) => s.sub_menu_link === "/cms/manage-customer/pending-payment")
    ?.can_approve === 1;
  const [viewMode, setViewMode] = useState("list"); // "list" | "grid"

  const ViewToggle = () => (
    <div style={{ display: "inline-flex", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
      <button
        onClick={() => setViewMode("list")}
        style={{
          padding: "6px 12px", border: "none", cursor: "pointer", fontSize: 13,
          background: viewMode === "list" ? "#6366f1" : "#fff",
          color: viewMode === "list" ? "#fff" : "#64748b",
          display: "flex", alignItems: "center", gap: 5,
          transition: "all 0.15s",
        }}
      >
        <UnorderedListOutlined /> List
      </button>
      <button
        onClick={() => setViewMode("grid")}
        style={{
          padding: "6px 12px", border: "none", borderLeft: "1px solid #e2e8f0", cursor: "pointer", fontSize: 13,
          background: viewMode === "grid" ? "#6366f1" : "#fff",
          color: viewMode === "grid" ? "#fff" : "#64748b",
          display: "flex", alignItems: "center", gap: 5,
          transition: "all 0.15s",
        }}
      >
        <AppstoreOutlined /> Grid
      </button>
    </div>
  );

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* Header */}
          <div style={{
            background: "linear-gradient(135deg,#6366f1,#4f46e5)",
            borderRadius: 16, padding: "24px 28px", marginBottom: 24,
            boxShadow: "0 8px 32px rgba(99,102,241,0.28)",
            display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 50, height: 50, background: "rgba(255,255,255,0.2)",
                borderRadius: 12, display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 22, color: "#fff",
              }}>
                <CreditCardOutlined />
              </div>
              <div>
                <h4 style={{ color: "#fff", margin: 0, fontSize: 22, fontWeight: 700 }}>Payment Approval Board</h4>
                <p style={{ color: "rgba(255,255,255,0.8)", margin: 0, fontSize: 13 }}>
                  {canAct ? "Review and approve / reject pending payments" : "Read-only view — contact an admin to approve payments"}
                </p>
              </div>
            </div>
            <ol className="breadcrumb m-0" style={{ background: "rgba(255,255,255,0.15)", borderRadius: 8, padding: "6px 14px" }}>
              <li className="breadcrumb-item"><Link to="/cms/home" style={{ color: "rgba(255,255,255,0.8)" }}>CMS</Link></li>
              <li className="breadcrumb-item active" style={{ color: "#fff" }}>Payment Approval</li>
            </ol>
          </div>

          {/* Role badge */}
          {!canAct && (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#fef9c3", border: "1px solid #fde68a",
              borderRadius: 8, padding: "8px 14px", marginBottom: 18, fontSize: 13, color: "#92400e",
            }}>
              <UserOutlined /> You are in read-only mode.
            </div>
          )}

          {/* Tabs */}
          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", padding: "0 20px 20px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 16, marginBottom: -8 }}>
              <ViewToggle />
            </div>
            <Tabs
              defaultActiveKey="pending"
              size="large"
              items={[
                {
                  key: "pending",
                  label: (
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <ClockCircleOutlined style={{ color: "#6366f1" }} /> Pending Payment
                    </span>
                  ),
                  children: <BoardColumn isRejected={false} canAct={canAct} viewMode={viewMode} />,
                },
                {
                  key: "rejected",
                  label: (
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <CloseCircleOutlined style={{ color: "#ef4444" }} /> Rejected
                    </span>
                  ),
                  children: <BoardColumn isRejected={true} canAct={canAct} viewMode={viewMode} />,
                },
                {
                  key: "approved",
                  label: (
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <CheckCircleOutlined style={{ color: "#16a34a" }} /> Approved
                    </span>
                  ),
                  children: <ApprovedColumn viewMode={viewMode} />,
                },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentApproval;
